// V5.32 : Composant Page Dossier Validé
// V5.45 : Ajout gestion PDL/PCE/Notes post-validation
// V5.67 : Barre de progression miroir + Consigne Expert en HAUT DE PAGE
// V5.68 : Upload fix + Dialogue expert

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, Download, Upload, FileText, Home, MapPin, Calendar,
  Loader2, AlertCircle, Shield, Clock, User, Building2,
  Zap, Save, Copy, Check, MessageCircle, Send, Truck
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

// ===================================================================
// V5.67 : BARRE DE PROGRESSION MIROIR (même logique que MesMissionsEnCours)
// ===================================================================
const PROGRESS_STEPS_VMV = [
  { id: 1, label: 'Lancement', statuses: ['nouveau', 'details_submitted', 'en_attente_documents', 'BROUILLON', 'pending', 'SIGNE', 'VALIDATED'] },
  { id: 2, label: 'Traitement', statuses: ['en_cours', 'en_cours_de_traitement', 'EN_COURS'] },
  { id: 3, label: 'Expédition', statuses: ['resiliation_envoyee'] },
  { id: 4, label: 'Résiliation', statuses: ['validation_resiliation'] },
  { id: 5, label: 'Effacé', statuses: ['termine', 'TERMINE'] }
];

const getStepVMV = (status) => {
  for (const step of PROGRESS_STEPS_VMV) {
    if (step.statuses.includes(status)) return step.id;
  }
  return 1;
};

const MissionProgressBarVMV = ({ status }) => {
  const currentStep = getStepVMV(status || 'nouveau');
  if (status === 'annule') return (
    <div className="p-2 bg-red-400/10 border border-red-400/30 rounded-lg text-center">
      <span className="text-red-400 text-xs font-mono">DOSSIER ANNULÉ</span>
    </div>
  );
  return (
    <div>
      <div className="flex items-center justify-between relative">
        <div className="absolute top-3 left-4 right-4 h-0.5 bg-gray-700" />
        <div
          className="absolute top-3 left-4 h-0.5 bg-cyan-400 transition-all duration-700"
          style={{ width: `${Math.max(0, ((currentStep - 1) / 4) * 100)}%` }}
        />
        {PROGRESS_STEPS_VMV.map((step) => {
          const isPast = step.id < currentStep;
          const isActive = step.id === currentStep;
          return (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                isPast ? 'bg-cyan-400 text-black' :
                isActive ? 'bg-cyan-400/30 border-2 border-cyan-400 text-cyan-400 animate-pulse' :
                'bg-gray-700 text-gray-500'
              }`}>
                {isPast ? <CheckCircle className="w-3.5 h-3.5" /> : step.id}
              </div>
              <span className={`text-[10px] mt-1 font-mono ${isPast || isActive ? 'text-cyan-400' : 'text-gray-600'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// V5.32 : Composant Page Dossier Validé
// V5.45 : Ajout gestion PDL/PCE/Notes post-validation
const ValidatedMissionView = ({ 
  mission, 
  missionData,
  user,
  onUploadDocument 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingMandat, setDownloadingMandat] = useState(false);
  const fileInputRef = useRef(null);
  
  // V5.45 : États pour les champs techniques (PDL/PCE/Notes)
  const [technicalData, setTechnicalData] = useState({ pdl: '', pce: '', notes: '' });
  const [savingTechnical, setSavingTechnical] = useState(false);
  const [technicalSaved, setTechnicalSaved] = useState(false);
  // V5.68 : Upload feedback + dialogue expert
  const [localUploadedDocs, setLocalUploadedDocs] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [localMessages, setLocalMessages] = useState([]);
  // V5.70 : Module Mission Accomplie
  const [avisText, setAvisText] = useState('');
  const [avisSubmitted, setAvisSubmitted] = useState(false);
  const [sendingAvis, setSendingAvis] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const isTermine = (mission?.admin_status || mission?.status) === 'termine';
  
  // V5.70 : Déclencher les confettis quand statut = termine
  useEffect(() => {
    if (isTermine && !showConfetti) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
  }, [isTermine]);
  
  // V5.45 : Charger les données techniques existantes
  useEffect(() => {
    if (missionData) {
      setTechnicalData({
        pdl: missionData?.categories?.electricite?.data?.pdl || mission?.details?.pdl_electricite || '',
        pce: missionData?.categories?.gaz?.data?.pce || mission?.details?.pce_gaz || '',
        notes: missionData?.notes || mission?.details?.form_data?.notes || ''
      });
    }
  }, [missionData, mission]);
  
  // V5.45 : Sauvegarder les données techniques
  const handleSaveTechnical = async () => {
    setSavingTechnical(true);
    try {
      const token = localStorage.getItem('token');
      const missionId = mission?._id || mission?.id || missionData?.id;
      
      await axios.post(`${API}/missions/${missionId}/technical`, {
        pdl: technicalData.pdl,
        pce: technicalData.pce,
        notes: technicalData.notes
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      setTechnicalSaved(true);
      toast.success('Données techniques enregistrées !');
      setTimeout(() => setTechnicalSaved(false), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde données techniques:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTechnical(false);
    }
  };
  
  // V5.45 : Copier un champ dans le presse-papiers
  const handleCopy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié !`);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };
  
  // V5.32 / DDD.8 : Extraction des adresses avec fallback robuste sur tous les emplacements
  // (step1_adresses, racine missionData, mission.details). Avant : la synthèse affichait "—"
  // si step1_adresses.arrivee.adresse était vide alors que la valeur existait à la racine.
  const rawAddresses = missionData?.step1_adresses || {};
  const fallbackArrivee = rawAddresses?.arrivee?.adresse
    || missionData?.nouvelle_adresse
    || mission?.details?.nouvelle_adresse
    || mission?.details?.new_address?.address
    || '';
  const fallbackDepart = rawAddresses?.depart?.adresse
    || missionData?.ancienne_adresse
    || mission?.details?.ancienne_adresse
    || mission?.details?.current_address?.address
    || '';
  const addresses = {
    ...rawAddresses,
    depart: {
      adresse: fallbackDepart,
      code_postal: rawAddresses?.depart?.code_postal || missionData?.ancienne_code_postal || mission?.details?.current_address?.postal_code || '',
      ville: rawAddresses?.depart?.ville || missionData?.ancienne_ville || mission?.details?.current_address?.city || '',
    },
    arrivee: {
      adresse: fallbackArrivee,
      code_postal: rawAddresses?.arrivee?.code_postal || missionData?.nouvelle_code_postal || mission?.details?.new_address?.postal_code || '',
      ville: rawAddresses?.arrivee?.ville || missionData?.nouvelle_ville || mission?.details?.new_address?.city || '',
    },
    date_demenagement: rawAddresses?.date_demenagement || missionData?.date_demenagement || mission?.details?.moving_date || '',
  };
  const categories = missionData?.categories || {};
  const signedAt = missionData?.signed_at || mission?.updated_at;
  
  // V5.32 : Extraire les contrats à activer et désactiver
  const getContractsByAction = () => {
    const activer = [];
    const desactiver = [];
    
    Object.entries(categories).forEach(([catId, catData]) => {
      if (catData?.status === 'active') {
        activer.push({
          id: catId,
          provider: catData.provider || catId,
          data: catData.data,
          techRef: catData.data ? Object.entries(catData.data).find(([k, v]) => v && !k.includes('deferred')) : null
        });
      } else if (catData?.status === 'desactive') {
        desactiver.push({
          id: catId,
          provider: catData.provider || catId,
          data: catData.data,
          techRef: catData.data ? Object.entries(catData.data).find(([k, v]) => v && !k.includes('deferred')) : null
        });
      }
    });
    
    return { activer, desactiver };
  };
  
  const { activer, desactiver } = getContractsByAction();
  
  // V5.32 : Télécharger le mandat PDF
  const handleDownloadMandat = async () => {
    // V5.33 FIX: Utiliser le mandat_id de la mission, pas l'ID de mission
    const mandatId = mission?.mandat_id || missionData?.mandat_id;
    
    if (!mandatId) {
      toast.error('Mandat non trouvé. Veuillez contacter le support.');
      console.error('Mandat ID manquant. Mission:', mission);
      return;
    }
    
    setDownloadingMandat(true);
    try {
      const token = localStorage.getItem('token');
      
      // V5.33 FIX: Utiliser la bonne route /api/mission-mandats/{mandat_id}/download
      const response = await axios.get(
        `${API}/mission-mandats/${mandatId}/download`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Créer le lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Mandat_L_Effaceur_${mandatId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Mandat téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur téléchargement mandat:', error);
      // V5.33 FIX: Message d'erreur plus explicite
      if (error.response?.status === 404) {
        toast.error('Le mandat n\'a pas encore été généré. Veuillez patienter.');
      } else {
        toast.error('Erreur lors du téléchargement du mandat');
      }
    } finally {
      setDownloadingMandat(false);
    }
  };
  
  // V5.32 / V5.68 : Upload de documents complémentaires (fix bug état local)
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', 'document_complementaire');
    formData.append('mission_id', mission?._id || mission?.id);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      // V5.68 FIX : Mise à jour IMMÉDIATE de l'état local sans attendre reload
      const newDoc = {
        filename: file.name,
        doc_type: 'document_complementaire',
        uploaded_at: new Date().toISOString(),
        document_id: res.data?.document_id || res.data?.id || `local_${Date.now()}`
      };
      setLocalUploadedDocs(prev => [...prev, newDoc]);
      toast.success(`Document "${file.name}" uploadé avec succès !`);
      onUploadDocument?.();
    } catch (error) {
      console.error('Erreur upload:', error);
      const errMsg = error.response?.data?.detail || 'Erreur lors de l\'upload';
      setUploadError(errMsg);
      toast.error(errMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset input pour permettre re-upload du même fichier
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // V5.68 : Envoyer une réponse à l'expert
  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const missionId = mission?._id || mission?.id;
      await axios.post(`${API}/missions/${missionId}/reply`, {
        message: replyText.trim()
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      // Mise à jour locale immédiate
      const newMsg = {
        id: Date.now(),
        author: 'client',
        text: replyText.trim(),
        date: new Date().toISOString()
      };
      setLocalMessages(prev => [...prev, newMsg]);
      setReplyText('');
      toast.success('Réponse envoyée à l\'expert !');
    } catch (err) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingReply(false);
    }
  };

  // V5.70 : Envoyer l'avis de fin de mission
  const handleSubmitAvis = async () => {
    if (!avisText.trim()) return;
    setSendingAvis(true);
    try {
      const token = localStorage.getItem('token');
      const missionId = mission?._id || mission?.id;
      await axios.post(`${API}/missions/${missionId}/avis`, { avis: avisText }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setAvisSubmitted(true);
      toast.success('Merci pour votre avis !');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingAvis(false);
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non spécifiée';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className="space-y-6 max-w-4xl mx-auto relative">

      {/* V5.70 : CONFETTIS CSS — Animation de célébration (statut=termine) */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ y: -20, x: Math.random() * window.innerWidth, opacity: 1, rotate: 0 }}
              animate={{ y: window.innerHeight + 50, opacity: 0, rotate: Math.random() * 720 }}
              transition={{ duration: 3 + Math.random() * 2, delay: Math.random() * 1.5, ease: 'easeIn' }}
              className="absolute w-3 h-3 rounded-sm"
              style={{ backgroundColor: ['#00F5FF', '#FF66CC', '#00FF88', '#FFD700', '#FF6B6B'][i % 5] }}
            />
          ))}
        </div>
      )}

      {/* V5.70 : MODULE "MISSION ACCOMPLIE" — affiché uniquement quand statut=termine */}
      {isTermine && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-6 border-2 border-success/60 bg-success/5"
          data-testid="mission-accomplie-block"
        >
          {/* Titre */}
          <div className="text-center mb-6">
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-5xl mb-3"
            >✦</motion.div>
            <h2 className="font-orbitron text-2xl font-bold text-success mb-1">Mission Accomplie</h2>
            <p className="text-gray-400">Félicitations — vos contrats ont été traités avec succès !</p>
          </div>

          {/* Parrainage actif */}
          <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-5 mb-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">✦</span>
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold font-orbitron text-sm mb-2">Programme Ambassadeur Actif</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full bg-success/20 text-success text-sm font-bold border border-success/30">-10%</span>
                    <span className="text-gray-300 text-sm">de réduction pour vos proches sur tout pack validé</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full bg-cyan-400/20 text-cyan-400 text-sm font-bold border border-cyan-400/30">5–10€</span>
                    <span className="text-gray-300 text-sm">crédités sur votre compte par parrainage validé</span>
                  </div>
                </div>
                <button
                  onClick={() => {/* handled by parent tab change */}}
                  className="mt-3 text-cyan-400 text-xs font-mono hover:text-cyan-300 flex items-center gap-1"
                  data-testid="parrainage-link-btn"
                >
                  Voir mon espace Parrainage →
                </button>
              </div>
            </div>
          </div>

          {/* Avis client */}
          <div className="space-y-3">
            <p className="text-white font-medium text-sm">Votre avis compte :</p>
            {avisSubmitted ? (
              <div className="p-4 bg-success/10 border border-success/30 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                <p className="text-success font-medium">Avis enregistré — Merci !</p>
              </div>
            ) : (
              <div className="flex gap-2">
                <textarea
                  value={avisText}
                  onChange={(e) => setAvisText(e.target.value)}
                  placeholder="Partagez votre expérience avec L'Effaceur... votre retour nous aide à progresser !"
                  rows={3}
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 text-sm focus:border-success/50 focus:outline-none resize-none"
                  data-testid="avis-textarea"
                />
                <Button
                  onClick={handleSubmitAvis}
                  disabled={sendingAvis || !avisText.trim()}
                  className="bg-success text-black hover:bg-success/80 font-bold self-end px-4"
                  data-testid="submit-avis-btn"
                >
                  {sendingAvis ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* V5.67/V5.68 : MODULE VIOLET "CONSIGNE EXPERT" + DIALOGUE */}
      {mission?.consigne_expert?.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-5 border-2 border-purple-400/60 bg-purple-400/5"
          data-testid="consigne-expert-block"
        >
          {/* Message de l'expert */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-purple-400/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-purple-400 font-mono text-sm font-bold">MESSAGE DE VOTRE EXPERT</p>
              {mission.consigne_expert.updated_at && (
                <p className="text-gray-500 text-xs">
                  {new Date(mission.consigne_expert.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
          <div className="p-3 bg-purple-400/10 rounded-lg mb-4">
            <p className="text-white leading-relaxed">{mission.consigne_expert.text}</p>
          </div>

          {/* Historique des réponses client */}
          {(() => {
            const allMsgs = [...(mission.details?.messages_thread || []), ...localMessages];
            return allMsgs.length > 0 && (
              <div className="mb-4 space-y-2">
                {allMsgs.map((msg, idx) => (
                  <div key={msg.id || idx} className={`p-3 rounded-lg text-sm ${msg.author === 'client' ? 'bg-cyan-400/10 border border-cyan-400/20 ml-4' : 'bg-purple-400/10 border border-purple-400/20'}`}>
                    <p className={`text-xs font-mono mb-1 ${msg.author === 'client' ? 'text-cyan-400' : 'text-purple-400'}`}>
                      {msg.author === 'client' ? 'Vous' : 'Expert'} — {new Date(msg.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-white">{msg.text}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Champ réponse rapide */}
          <div className="flex gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
              placeholder="Réponse rapide à l'expert..."
              className="flex-1 bg-gray-900 border border-purple-400/30 rounded-lg px-3 py-2 text-white placeholder:text-gray-500 text-sm focus:border-purple-400 focus:outline-none"
              data-testid="client-reply-input"
            />
            <Button
              onClick={handleSendReply}
              disabled={sendingReply || !replyText.trim()}
              className="bg-purple-500 hover:bg-purple-400 text-white px-4"
              data-testid="send-reply-btn"
            >
              {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </motion.div>
      )}

      {/* V5.67 : BARRE DE PROGRESSION MIROIR — synchronisée avec le statut admin */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="glass-card p-4 border border-cyan-400/30 bg-cyan-400/3"
        data-testid="mission-progress-bar-manage"
      >
        <p className="text-cyan-400 text-xs font-mono mb-3">// PROGRESSION DE VOTRE DOSSIER</p>
        <MissionProgressBarVMV status={mission?.admin_status || mission?.status || 'nouveau'} />
      </motion.div>

      {/* V1.2 PREMIUM : Dossier consolidé — en-tête + référence + mandant dans UN seul bloc */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 md:p-8 border border-cyan-400/30 relative overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(0,245,255,0.08)' }}
      >
        {/* Gradient décoratif */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 via-cyan-400/50 to-transparent" />

        {/* Ligne 1 : badge statut + référence + date */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 border border-success/30 mb-3">
              <CheckCircle className="w-3.5 h-3.5 text-success" />
              <span className="text-success text-xs font-medium">Dossier transmis — Expert Toulousain</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-orbitron font-bold text-white leading-tight">
              Tableau de Bord de Mission
            </h1>
            <p className="text-gray-500 font-mono text-[10px] tracking-widest mt-1">// VOTRE_DOSSIER_EN_TEMPS_RÉEL</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-gray-500 text-[10px] font-mono tracking-widest">RÉFÉRENCE</p>
              <p className="text-cyan-400 font-orbitron font-bold text-base">{mission?.display_id || missionData?.id || 'N/A'}</p>
            </div>
            <div className="h-10 w-px bg-gray-700" />
            <div className="text-right">
              <p className="text-gray-500 text-[10px] font-mono tracking-widest">VALIDÉ LE</p>
              <p className="text-white font-medium text-sm">{formatDate(signedAt)}</p>
            </div>
          </div>
        </div>

        {/* Ligne 2 : Mandant (3 champs compacts, pas de sous-cartes superflues) */}
        <div className="grid md:grid-cols-3 gap-x-8 gap-y-3 pt-4 border-t border-gray-800/60">
          <div>
            <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">MANDANT</p>
            <p className="text-white font-medium truncate">
              {(() => {
                const fullName = missionData?.nom_prenom
                  || missionData?.client?.nom_prenom
                  || `${missionData?.first_name || missionData?.client?.first_name || ''} ${missionData?.last_name || missionData?.client?.last_name || ''}`.trim();
                return fullName || '—';
              })()}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">TÉLÉPHONE</p>
            <p className="text-white font-medium">{missionData?.telephone || missionData?.client?.telephone || '—'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">EMAIL</p>
            <p className="text-white font-medium text-sm truncate">{mission?.user_email || missionData?.email || missionData?.client?.email || '—'}</p>
          </div>
        </div>
      </motion.div>
      
      {/* V1.2 : Bloc adapté selon le type de mission (Déménagement vs Héritage) */}
      {(() => {
        // V1.2 : détection Succession via plusieurs chemins possibles
        const packType = (
          mission?.pack_type
          || mission?.mission?.pack_type
          || mission?.details?.pack_type
          || missionData?.pack_type
          || ''
        );
        const missionTypeField = mission?.mission_type || mission?.type || '';
        const isSuccessionView = /heritage|succession|serenite/i.test(packType)
          || /heritage|succession|serenite/i.test(missionTypeField)
          || (missionData?.defunt || missionData?.nom_defunt || missionData?.prenom_defunt)
          || (mission?.details?.deceased);

        if (isSuccessionView) {
          const defunt = missionData?.defunt || missionData?.client || mission?.details?.deceased || mission?.deceased || {};
          const prenomDefunt = defunt?.prenom_defunt || defunt?.first_name || missionData?.prenom_defunt || '';
          const nomDefunt = defunt?.nom_defunt || defunt?.last_name || defunt?.name || missionData?.nom_defunt || '';
          const fullNom = `${prenomDefunt} ${nomDefunt}`.trim() || '—';
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 border border-magenta-400/30 bg-magenta-400/5 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-magenta-400 via-magenta-400/50 to-transparent" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-magenta-400/15 border border-magenta-400/30 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-magenta-400" />
                </div>
                <div>
                  <h3 className="font-orbitron text-base font-bold text-white">Informations du défunt</h3>
                  <p className="text-gray-500 text-[10px] font-mono tracking-widest">// SUCCESSION</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-x-8 gap-y-3">
                <div>
                  <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">NOM DU DÉFUNT</p>
                  <p className="text-white font-medium text-base">{fullNom}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">DATE DU DÉCÈS</p>
                  <p className="text-white font-medium">{formatDate(defunt?.date_deces || defunt?.death_date || missionData?.date_deces)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] font-mono tracking-widest mb-0.5">LIEN DE PARENTÉ</p>
                  <p className="text-white font-medium capitalize">{defunt?.lien_parente || defunt?.relation || missionData?.lien_parente || '—'}</p>
                </div>
              </div>
            </motion.div>
          );
        }

        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border border-cyan-400/30 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 via-cyan-400/50 to-transparent" />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-orbitron text-base font-bold text-white">Logistique du déménagement</h3>
                <p className="text-gray-500 text-[10px] font-mono tracking-widest">// DÉPART_ARRIVÉE_DATE</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="rounded-lg border-l-2 border-red-400 bg-red-400/5 px-4 py-3">
                <p className="text-red-400 text-[10px] font-mono tracking-widest mb-0.5">DÉPART</p>
                <p className="text-white font-medium leading-tight">{addresses.depart?.adresse || '—'}</p>
                <p className="text-gray-400 text-xs mt-0.5">{addresses.depart?.code_postal} {addresses.depart?.ville}</p>
              </div>
              <div className="rounded-lg border-l-2 border-cyan-400 bg-cyan-400/5 px-4 py-3">
                <p className="text-cyan-400 text-[10px] font-mono tracking-widest mb-0.5">ARRIVÉE</p>
                <p className="text-white font-medium leading-tight">{addresses.arrivee?.adresse || '—'}</p>
                <p className="text-gray-400 text-xs mt-0.5">{addresses.arrivee?.code_postal} {addresses.arrivee?.ville}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-gray-800/40 border border-gray-700/40">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 flex items-center justify-between">
                <span className="text-gray-500 text-[10px] font-mono tracking-widest">DATE DU DÉMÉNAGEMENT</span>
                <span className="text-white font-medium text-sm">{formatDate(addresses.date_demenagement)}</span>
              </div>
            </div>
          </motion.div>
        );
      })()}
      
      {/* V1.2 PREMIUM : Contrats concernés — carte unifiée compacte avec 2 sections */}
      {(activer.length > 0 || desactiver.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 border border-gray-700 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 via-gray-600 to-red-400" />
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center">
              <FileText className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-orbitron text-base font-bold text-white">Contrats concernés</h3>
              <p className="text-gray-500 text-[10px] font-mono tracking-widest">// {activer.length + desactiver.length} fournisseur(s) sélectionné(s)</p>
            </div>
            <div className="flex items-center gap-3 text-xs font-mono">
              {activer.length > 0 && (
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {activer.length} transfert{activer.length > 1 ? 's' : ''}
                </span>
              )}
              {desactiver.length > 0 && (
                <span className="flex items-center gap-1.5 text-red-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> {desactiver.length} résiliation{desactiver.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {activer.length > 0 && (
              <div className="rounded-lg border-l-2 border-emerald-400/60 bg-emerald-400/5 px-4 py-3">
                <p className="text-emerald-400 text-[10px] font-mono tracking-widest mb-2 flex items-center gap-1.5">
                  <Check className="w-3 h-3" /> TRANSFERT
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {activer.map((c, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-emerald-400/10 border border-emerald-400/20 text-emerald-300 text-xs font-medium">
                      {c.provider}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {desactiver.length > 0 && (
              <div className="rounded-lg border-l-2 border-red-400/60 bg-red-400/5 px-4 py-3">
                <p className="text-red-400 text-[10px] font-mono tracking-widest mb-2 flex items-center gap-1.5">
                  <span className="text-base leading-none">✗</span> RÉSILIATION
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {desactiver.map((c, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-red-400/10 border border-red-400/20 text-red-300 text-xs font-medium">
                      {c.provider}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
      
      {/* V5.69 PREMIUM : Données Techniques */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3"
      >
        <h3 className="font-orbitron text-xs text-gray-500 tracking-widest px-1">// DONNÉES TECHNIQUES</h3>
        <div className="rounded-xl border border-yellow-400/20 bg-yellow-400/3 p-5">
          <p className="text-gray-400 text-sm mb-4">
            Renseignez ces informations dès réception par SMS de vos fournisseurs.
          </p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* PDL - Point de Livraison Électricité */}
          <div className="space-y-2">
            <label className="text-yellow-400 text-xs font-mono flex items-center gap-2">
              <Zap className="w-3 h-3" />
              PDL (Point de Livraison Électricité)
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: 12345678901234"
                value={technicalData.pdl}
                onChange={(e) => setTechnicalData(prev => ({ ...prev, pdl: e.target.value }))}
                className="bg-gray-800/50 border-gray-700 flex-1"
                data-testid="pdl-input-postval"
              />
              {technicalData.pdl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(technicalData.pdl, 'PDL')}
                  className="px-3"
                >
                  <Copy className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
            <p className="text-gray-500 text-xs">14 chiffres - Visible sur votre facture ou compteur Linky</p>
          </div>
          
          {/* PCE - Point de Comptage Gaz */}
          <div className="space-y-2">
            <label className="text-orange-400 text-xs font-mono flex items-center gap-2">
              <Zap className="w-3 h-3" />
              PCE (Point de Comptage Gaz)
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: GI123456"
                value={technicalData.pce}
                onChange={(e) => setTechnicalData(prev => ({ ...prev, pce: e.target.value }))}
                className="bg-gray-800/50 border-gray-700 flex-1"
                data-testid="pce-input-postval"
              />
              {technicalData.pce && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(technicalData.pce, 'PCE')}
                  className="px-3"
                >
                  <Copy className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
            <p className="text-gray-500 text-xs">Visible sur votre facture de gaz</p>
          </div>
        </div>
        
        {/* Notes complémentaires */}
        <div className="space-y-2 mb-4">
          <label className="text-cyan-400 text-xs font-mono">
            Informations complémentaires (Notes)
          </label>
          <textarea
            placeholder="Ex: Index de relevé, numéro de client, précisions pour l'Expert..."
            value={technicalData.notes}
            onChange={(e) => setTechnicalData(prev => ({ ...prev, notes: e.target.value }))}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-white placeholder:text-gray-500 min-h-[100px] focus:border-cyan-400 focus:outline-none"
            data-testid="notes-input-postval"
          />
        </div>
        
        {/* Bouton sauvegarder */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveTechnical}
            disabled={savingTechnical}
            className={`font-bold ${technicalSaved ? 'bg-green-500 hover:bg-green-400' : 'bg-yellow-400 hover:bg-yellow-300'} text-black`}
            data-testid="save-technical-btn"
          >
            {savingTechnical ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : technicalSaved ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {technicalSaved ? 'Enregistré !' : 'Sauvegarder'}
          </Button>
        </div>
        </div>
      </motion.div>
      
      {/* V1.2 PREMIUM : Téléchargement Mandat — bloc mis en avant avec DA cohérente */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border border-success/30 relative overflow-hidden"
        style={{ boxShadow: '0 0 30px rgba(0,255,148,0.08)' }}
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-success via-success/50 to-transparent" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-success/15 border border-success/30 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-success" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-orbitron font-bold text-base">Mandat de Gestion signé</h3>
                <span className="px-2 py-0.5 bg-success/20 text-success text-[10px] rounded font-mono tracking-wider">CERTIFIÉ</span>
              </div>
              <p className="text-gray-400 text-sm mt-1">
                Document juridique horodaté — Art. 1984 Code civil. Conservez-le précieusement.
              </p>
            </div>
          </div>
          <Button
            onClick={handleDownloadMandat}
            disabled={downloadingMandat}
            className="bg-success text-black hover:bg-success/90 font-bold flex-shrink-0"
            data-testid="download-mandat-btn"
          >
            {downloadingMandat ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Téléchargement…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Télécharger mon mandat (PDF)</>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Zone d'upload Documents Complémentaires */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border border-gray-700"
      >
        <h3 className="font-orbitron text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-cyan-400" />
          Documents complémentaires
        </h3>
        
        <p className="text-gray-400 text-sm mb-4">
          Si notre Expert vous demande des documents supplémentaires, uploadez-les ici.
        </p>
        
        {/* V5.68 : Affichage immédiat des docs uploadés cette session */}
        {localUploadedDocs.length > 0 && (
          <div className="mb-4 space-y-2">
            {localUploadedDocs.map((doc, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-success/10 border border-success/30 rounded-lg">
                <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                <span className="text-white text-sm truncate">{doc.filename}</span>
                <span className="text-success text-xs ml-auto">Envoyé</span>
              </div>
            ))}
          </div>
        )}
        
        {uploadError && (
          <div className="mb-4 p-3 bg-red-400/10 border border-red-400/30 rounded-lg">
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {uploadError}
            </p>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={handleFileUpload}
          className="hidden"
        />
        
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="w-full border-dashed border-gray-600 hover:border-cyan-400/50 py-6"
          data-testid="upload-document-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Upload en cours... {uploadProgress}%
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Ajouter un document
            </>
          )}
        </Button>
        
        {/* Barre de progression */}
        {uploading && uploadProgress > 0 && (
          <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-cyan-400"
              initial={{ width: 0 }}
              animate={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </motion.div>
      
      {/* V5.32 : Note de contact */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-4 bg-gray-800/30 rounded-lg border border-gray-700 flex items-start gap-3"
      >
        <Shield className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white font-medium">Besoin d'aide ?</p>
          <p className="text-gray-400 text-sm">
            Notre Expert Toulousain traite votre dossier. Si vous avez des questions, 
            utilisez le chatbot en bas à droite ou contactez-nous.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ValidatedMissionView;
